import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import PaginatedSortableTable from "../components/PaginatedSortableTable";
import DateTimeRangePicker from "../components/DateTimeRangePicker";
import {
  getInitialDateTimeRange,
  getPresetDateTimeRange,
} from "../utils/dateRange";

export default function MyMoney() {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const initialRange = getInitialDateTimeRange();
  const [startDateTime, setStartDateTime] = useState(initialRange.start);
  const [endDateTime, setEndDateTime] = useState(initialRange.end);
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { setIsLoggedIn } = useOutletContext();
  const navigate = useNavigate();

  const sortOptions = useMemo(
    () => [
      { value: "snapshot_at:desc", label: "처리시간 - 내림차순" },
      { value: "snapshot_at:asc", label: "처리시간 - 오름차순" },
      { value: "diff_amt:desc", label: "변동금액 - 내림차순" },
      { value: "diff_amt:asc", label: "변동금액 - 오름차순" },
    ],
    []
  );
  const totalDiffAmt = useMemo(
    () => logs.reduce((sum, row) => sum + Number(row.diff_amt || 0), 0),
    [logs]
  );
  const totalDiffClass =
    totalDiffAmt > 0
      ? "text-success"
      : totalDiffAmt < 0
        ? "text-danger"
        : "text-muted";
  const totalDiffPrefix = totalDiffAmt > 0 ? "+" : "";

  const getSnapshotAtParts = (row) => {
    const raw = row.snapshot_at_text || row.snapshot_at;
    if (!raw) return { date: "-", time: "-" };

    if (typeof raw === "string") {
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)) {
        const [date, time] = raw.split(" ");
        return { date, time };
      }

      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(raw)) {
        const normalized = raw.slice(0, 19).replace("T", " ");
        const [date, time] = normalized.split(" ");
        return { date, time };
      }
    }

    return { date: String(raw), time: "" };
  };

  const fetchMyMoneyHistory = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/api/MyMoneyHistory`, {
        params: {
          start: `${startDateTime.replace("T", " ")}:00`,
          end: `${endDateTime.replace("T", " ")}:59`,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLogs(Array.isArray(res.data?.logs) ? res.data.logs : []);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if (
          err.response.status === 401 ||
          err.response.status === 403 ||
          err.response.status === 500
        ) {
          localStorage.removeItem("token");
          setIsLoggedIn(false);
          navigate("/login");
          return;
        }
      }
      console.error("MyMoney 조회 실패:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMyMoneyHistory();
    const intervalId = setInterval(() => {
      fetchMyMoneyHistory();
    }, 60000);

    return () => clearInterval(intervalId);
  }, [startDateTime, endDateTime]);

  const handleDateRange = (type) => {
    const range = getPresetDateTimeRange(type);
    setStartDateTime(range.start);
    setEndDateTime(range.end);
  };

  const renderRow = (row, idx) => {
    const diff = Number(row.diff_amt || 0);
    const possessAmt = Number(row.possess_amt || 0);
    const beforeAmt = possessAmt - diff;
    const diffClass =
      diff > 0 ? "text-success" : diff < 0 ? "text-danger" : "text-muted";
    const diffPrefix = diff > 0 ? "+" : "";
    const { date, time } = getSnapshotAtParts(row);

    return (
      <tr key={`${row.user_id}-${row.snapshot_at}-${idx}`} data-pos-y={idx}>
        <td className="text-end">{beforeAmt.toLocaleString()}</td>
        <td className={`text-end ${diffClass}`}>
          {diffPrefix}
          {diff.toLocaleString()}
        </td>
        <td className="text-end">{possessAmt.toLocaleString()}</td>
        <td className="text-center">
          <span style={{ fontSize: "95%", whiteSpace: "nowrap" }}>
            {date}
            <br />
            {time ? (
              <font className="dateInHour">({time} UTC +9)</font>
            ) : null}
          </span>
        </td>
      </tr>
    );
  };

  return (
    <div className="nk-content user_view_wrap">
      <div className="container-fluid">
        <div className="nk-content-inner">
          <div className="nk-content-body">
            <div className="nk-block nk-block-lg vTrxsAll" id="opt-x">
              <div className="card card-bordered">
                <div className="card-inner-group">
                  <div className="card-inner p-3">
                    <div className="nk-block nk-block-lg">
                      <form
                        className="mb-0"
                        onSubmit={(e) => {
                          e.preventDefault();
                          fetchMyMoneyHistory();
                        }}
                      >
                        <DateTimeRangePicker
                          startDateTime={startDateTime}
                          endDateTime={endDateTime}
                          onStartChange={setStartDateTime}
                          onEndChange={setEndDateTime}
                          onPresetChange={handleDateRange}
                        >
                          <div className="col-auto">
                            <button
                              type="submit"
                              className="btn btn-outline-primary btn-dim"
                              disabled={isLoading}
                            >
                              <em className="icon ni ni-search me-2" />
                              조회
                            </button>
                          </div>
                          <div
                            className="col-auto d-flex align-items-center"
                            style={{ marginLeft: "20px" }}
                          >
                            <span className="sub-text">
                              총 변동금액{" "}
                              <strong className={totalDiffClass}>
                                {totalDiffPrefix}
                                {totalDiffAmt.toLocaleString()}
                              </strong>{" "}
                              / {logs.length}건
                            </span>
                          </div>
                        </DateTimeRangePicker>
                      </form>
                    </div>
                  </div>

                  <div className="card-inner tableBox px-0 pb-0">
                    <div className="table-wrapper">
                      <div className="table-responsive">
                        <div data-pattern="priority-columns">
                          <table
                            cellSpacing="0"
                            className="table table-small-font table-tighten table-bordered table-striped"
                          >
                            <thead>
                              <tr data-pos-y="0">
                                <th className="text-center">
                                  <span className="sub-text text-center">
                                    변동전 금액
                                  </span>
                                </th>
                                <th className="text-center">
                                  <span className="sub-text text-center">
                                    변동금액
                                  </span>
                                </th>
                                <th className="text-center">
                                  <span className="sub-text text-center">
                                    변동후 잔액
                                  </span>
                                </th>
                                <th className="text-center">
                                  <span className="sub-text text-center">
                                    처리시각
                                  </span>
                                </th>
                              </tr>
                            </thead>

                            <PaginatedSortableTable
                              data={logs}
                              renderRow={renderRow}
                              defaultSortField="snapshot_at"
                              defaultSortOrder="desc"
                              sortOptions={sortOptions}
                            />
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
